// US-GOAL-01/02 -- Child views and manages savings and giving goals.

import {
  type BucketEntry,
  type SavingsGoal,
  bucketsList,
  goalCancel,
  goalComplete,
  goalCreate,
  goalsList,
} from '@chorepop/core/client';
import { type Component, For, Show, createMemo, createResource, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';
import { ReportContentButton } from '../components/ReportContentButton';
import { burstGoalStars } from '../lib/childCelebration';
import { goalStarBurstsEnabled } from '../state/childCelebrationPreferences';
import { isFamilyContentHidden } from '../state/familyContentVisibility';

const EMOJI_OPTIONS = ['🚲', '🎮', '📚', '✈️', '🎸', '👟', '🏆', '🌟', '🎯', '💎'];

type Props = {
  sessionId: string;
  currency: string;
  /** 'saving' (default) or 'giving' */
  goalType?: 'saving' | 'giving';
  onBack: () => void;
};

const MILESTONES = [
  { pct: 100, label: 'Goal reached!', emoji: '\u{1F389}', colour: '#15803d' },
  { pct: 75, label: '75% of the way there!', emoji: '\u{1F4AA}', colour: '#0369a1' },
  { pct: 50, label: 'Halfway there!', emoji: '\u{1F31F}', colour: '#7c3aed' },
  { pct: 25, label: 'Great start -- 25% done!', emoji: '\u{1F44D}', colour: '#ea580c' },
];

function milestoneBanner(pct: number): { label: string; emoji: string; colour: string } | null {
  return MILESTONES.find((m) => pct >= m.pct) ?? null;
}

export const GoalsScreen: Component<Props> = (props) => {
  const [selectedGoalType, setSelectedGoalType] = createSignal<'saving' | 'giving'>(
    props.goalType ?? 'saving',
  );
  const isGiving = () => selectedGoalType() === 'giving';
  const [buckets] = createResource(async () => {
    const r = await bucketsList({ sessionId: props.sessionId });
    return r.ok ? r.value : ([] as BucketEntry[]);
  });

  const [goals, { refetch }] = createResource(
    () => props.sessionId,
    (sid) => goalsList(sid).then((r) => (r.ok ? r.value : [])),
  );
  // The main My goals destination shows every goal. `selectedGoalType` is
  // only the type being created; using it as the list filter caused choosing
  // or defaulting to Savings to hide existing Giving goals.
  const visibleGoals = createMemo(() => {
    const allGoals = goals() ?? [];
    return props.goalType ? allGoals.filter((goal) => goal.goalType === props.goalType) : allGoals;
  });

  const [showForm, setShowForm] = createSignal(false);
  const [showGoalTypePicker, setShowGoalTypePicker] = createSignal(false);
  const [goalName, setGoalName] = createSignal('');
  const [goalNotes, setGoalNotes] = createSignal('');
  const [targetAmount, setTargetAmount] = createSignal('');
  const [bucketId, setBucketId] = createSignal<number | null>(null);
  const [emoji, setEmoji] = createSignal('🌟');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const formatMajor = (minor: number) => (minor / 100).toFixed(2);

  const handleCreate = async (e: Event) => {
    e.preventDefault();
    if (!goalName().trim()) {
      setError('Goal name is required.');
      return;
    }
    const target = Math.round(Number.parseFloat(targetAmount() || '0') * 100);
    if (target <= 0) {
      setError('Target must be greater than zero.');
      return;
    }
    setBusy(true);
    setError(null);
    const selectedBucketId = bucketId();
    const r = await goalCreate({
      sessionId: props.sessionId,
      ...(selectedBucketId != null ? { bucketId: selectedBucketId } : {}),
      goalName: goalName().trim(),
      ...(goalNotes().trim() ? { goalNotes: goalNotes().trim() } : {}),
      targetMinorUnits: target,
      goalEmoji: emoji(),
      goalType: selectedGoalType(),
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setShowForm(false);
    setGoalName('');
    setTargetAmount('');
    setBucketId(null);
    void refetch();
  };

  const handleComplete = async (g: SavingsGoal, origin?: DOMRectReadOnly) => {
    if (!confirm(`Mark "${g.goalName}" as complete?`)) return;
    setBusy(true);
    const result = await goalComplete(props.sessionId, g.goalId);
    setBusy(false);
    if (result.ok && goalStarBurstsEnabled()) burstGoalStars(origin);
    void refetch();
  };

  const handleCancel = async (g: SavingsGoal) => {
    if (!confirm(`Cancel "${g.goalName}"?`)) return;
    setBusy(true);
    await goalCancel(props.sessionId, g.goalId);
    setBusy(false);
    void refetch();
  };

  return (
    <ActionScreen
      class="action-screen--goal"
      eyebrow="Goals"
      title="My goals"
      onBack={props.onBack}
    >
      <Show when={error()}>
        <div class="form-error" role="alert">
          {error()}
        </div>
      </Show>

      <Show when={!showForm()}>
        <section class="goal-create-card" aria-label="Add a goal">
          <button
            type="button"
            onClick={() => setShowGoalTypePicker(true)}
            data-testid="add-goal-btn"
          >
            + Add goal
          </button>
        </section>
      </Show>

      <Show when={showGoalTypePicker() && !showForm()}>
        <section class="goal-type-picker" aria-label="Choose goal type">
          <h2>What kind of goal is this?</h2>
          <p>Both are your choice — pick the one that fits this goal.</p>
          <div class="goal-type-picker-actions">
            <button
              type="button"
              class="action-chip"
              onClick={() => {
                setSelectedGoalType('saving');
                setShowGoalTypePicker(false);
                setShowForm(true);
              }}
            >
              Savings goal
            </button>
            <button
              type="button"
              class="action-chip"
              onClick={() => {
                setSelectedGoalType('giving');
                setShowGoalTypePicker(false);
                setShowForm(true);
              }}
            >
              Giving goal
            </button>
          </div>
        </section>
      </Show>

      <Show when={showForm()}>
        <form onSubmit={handleCreate} class="action-form">
          <h2>{isGiving() ? 'Who or what would you like to help?' : 'What are you saving for?'}</h2>
          <div class="field-row">
            <span>Pick an icon</span>
            <div class="goal-emoji-row">
              <For each={EMOJI_OPTIONS}>
                {(opt) => (
                  <button
                    type="button"
                    class={`goal-emoji-btn${emoji() === opt ? ' goal-emoji-btn--on' : ''}`}
                    onClick={() => setEmoji(opt)}
                  >
                    {opt}
                  </button>
                )}
              </For>
            </div>
          </div>
          <div class="field-row">
            <label for="goal-name">Goal name *</label>
            <input
              id="goal-name"
              type="text"
              maxlength="60"
              placeholder="e.g. New bicycle"
              value={goalName()}
              onInput={(e) => setGoalName(e.currentTarget.value)}
            />
          </div>
          <div class="field-row">
            <label for="goal-target">Target amount ({props.currency}) *</label>
            <input
              id="goal-target"
              type="number"
              min="0.01"
              step="0.01"
              value={targetAmount()}
              onInput={(e) => setTargetAmount(e.currentTarget.value)}
            />
          </div>
          <div class="field-row">
            <label for="goal-bucket">Track progress from bucket (optional)</label>
            <select
              id="goal-bucket"
              value={bucketId() ?? ''}
              onChange={(e) => setBucketId(Number(e.currentTarget.value) || null)}
            >
              <option value="">-- no bucket --</option>
              <For each={buckets() ?? []}>
                {(b: BucketEntry) => <option value={b.bucketId}>{b.bucketName}</option>}
              </For>
            </select>
          </div>
          <div class="field-row">
            <label for="goal-notes">Notes (optional)</label>
            <input
              id="goal-notes"
              type="text"
              maxlength="200"
              value={goalNotes()}
              onInput={(e) => setGoalNotes(e.currentTarget.value)}
            />
          </div>
          <div class="action-form-actions">
            <button type="submit" class="primary action-screen-primary" disabled={busy()}>
              Create goal
            </button>
            <button
              type="button"
              class="ghost"
              onClick={() => {
                setShowForm(false);
                setShowGoalTypePicker(false);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </Show>

      <Show when={!goals.loading && visibleGoals().length === 0 && !showForm()}>
        <div class="action-empty-state">
          <p style="font-size: 2rem; margin: 0;">🌟</p>
          <p>
            {props.goalType === 'giving'
              ? 'No giving goals yet. Create one for a cause you care about!'
              : 'No goals yet. Create a savings goal or a giving goal to get started!'}
          </p>
        </div>
      </Show>

      <ul class="goal-list action-goal-list">
        <For each={visibleGoals().filter((goal) => !isFamilyContentHidden(`goal:${goal.goalId}`))}>
          {(g) => (
            <li class="goal-card" data-testid={`goal-${g.goalId}`}>
              <div class="goal-card-header">
                <span class="goal-emoji">{g.goalEmoji ?? '🌟'}</span>
                <div class="goal-card-titles">
                  <span class="goal-name">{g.goalName}</span>
                  <span class="goal-type-label">
                    {g.goalType === 'giving' ? 'Giving goal' : 'Savings goal'}
                  </span>
                  <Show when={g.bucketName}>
                    <span class="goal-bucket-label">from {g.bucketName}</span>
                  </Show>
                </div>
                <span class="goal-pct">{g.progressPct}%</span>
              </div>
              {/* Milestone celebration banner */}
              {(() => {
                const ms = milestoneBanner(g.progressPct);
                if (!ms) return null;
                return (
                  <div
                    class="goal-milestone"
                    style={`border-color: ${ms.colour}; color: ${ms.colour};`}
                  >
                    <span>{ms.emoji}</span>
                    <span>{ms.label}</span>
                  </div>
                );
              })()}

              {/* Progress bar */}
              <div class="goal-progress-track">
                <div class="goal-progress-fill" style={`width: ${g.progressPct}%`} />
              </div>
              <div class="goal-amounts">
                <span class="goal-current">
                  {props.currency} {formatMajor(g.currentMinorUnits)} saved
                </span>
                <span class="goal-target">
                  of {props.currency} {formatMajor(g.targetMinorUnits)}
                </span>
              </div>
              <div class="goal-actions">
                <ReportContentButton
                  context={{
                    contentKind: 'goal',
                    contentLocalId: `goal:${g.goalId}`,
                    label: `${g.goalType === 'giving' ? 'giving' : 'savings'} goal “${g.goalName}”`,
                    contentSnapshot: {
                      goalId: g.goalId,
                      childAccountId: g.childAccountId,
                      goalName: g.goalName,
                      goalNotes: g.goalNotes,
                      goalType: g.goalType,
                      goalEmoji: g.goalEmoji,
                    },
                  }}
                />
                <Show when={g.progressPct >= 100}>
                  <button
                    type="button"
                    class="primary"
                    disabled={busy()}
                    onClick={(event) =>
                      void handleComplete(g, event.currentTarget.getBoundingClientRect())
                    }
                  >
                    Mark complete!
                  </button>
                </Show>
                <button
                  type="button"
                  class="ghost"
                  style="color: var(--color-danger);"
                  disabled={busy()}
                  onClick={() => handleCancel(g)}
                >
                  Cancel
                </button>
              </div>
            </li>
          )}
        </For>
      </ul>
    </ActionScreen>
  );
};
